import { MouseEvent, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { find, map } from 'lodash-es';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import EditionYearFilter from 'src/common/components/dashboard/EditionYearFilter';
import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';

import { useAuth } from 'src/auth/useAuth';
import Empty from 'src/common/components/Empty';
import { ticketCardGridContainerPaddingSx, ticketCardGridItemSx } from 'src/common/styles/ticketCardStyles';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import ViewSwitch from 'src/common/components/ViewSwitch';
import usePersistentView from 'src/hooks/usePersistentView';
import { IEdition } from 'src/interfaces/Edition';
import { EView } from 'src/interfaces/View';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import EditionAccountingSection from './components/EditionAccountingSection';
import EditionCard from './components/EditionCard';
import { IFormEdition } from 'src/interfaces/Edition';
import EditionEditorModal from './modals/EditionEditorModal';
import EditionsTable from './tables/EditionsTable';
const Editions = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const {
    activeEditionId,
    editions,
    editionsById,
    deleteEdition: doDeleteEdition,
    deleteAllEditions: doDeleteAllEditions,
    isLoading: isLoadingSystemOptions,
    saveEdition: doSaveEdition,
    toggleActiveEdition: doToggleActiveEdition,
    saveStatisticsForEdition: doSaveStatisticsForEdition,
  } = useSystemOptionsStore(systemOptionsState => systemOptionsState);
  const { isSuperAdmin } = useAuth();

  const [view, setView] = usePersistentView();
  const [editionEditorModalOpen, setEditionEditorModalOpen] = useState(false);
  const [editionToEdit, setEditionToEdit] = useState<IEdition | undefined>(undefined);
  const [isLoadingEdition, setIsLoadingEdition] = useState<{ [key: string]: boolean }>();
  const [accountingYear, setAccountingYear] = useState<number | null>(null);

  useEffect(() => {
    if (accountingYear != null) return;
    const activeEdition = activeEditionId ? editionsById?.[activeEditionId] : undefined;
    const editionForYear = activeEdition ?? editions[0];
    if (editionForYear) {
      setAccountingYear(editionForYear.year);
    }
  }, [accountingYear, activeEditionId, editions, editionsById]);

  const accountingEdition = useMemo(() => {
    if (accountingYear == null) return undefined;
    return find(editions, { year: accountingYear });
  }, [accountingYear, editions]);

  const handleOpenEditionEditorModal = (id?: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const edition = find(editions, { id });
    setEditionToEdit(edition);
    setEditionEditorModalOpen(true);
  };

  const handleCloseEditionEditorModal = () => {
    setEditionToEdit(undefined);
    setEditionEditorModalOpen(false);
  };

  const saveEdition = async (editedEdition: IFormEdition, id?: string) => {
    await doSaveEdition(editedEdition, id).then(() =>
      enqueueSnackbar(t('editions.saveSuccessful'), {
        variant: 'success',
      }),
    );
    handleCloseEditionEditorModal();
  };

  const handleDeleteEdition = async (id: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const { confirmed } = await confirm({
      title: t('editions.deleteConfirm'),
      description: t('editions.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t('editions.deleteErrorMessage'), {
        variant: 'error',
      });
      return;
    }
    await doDeleteEdition(id);

    enqueueSnackbar(t('editions.deleteSuccessfulMessage'), {
      variant: 'success',
    });
  };

  const handleDeleteAllEditions = async (e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const { confirmed } = await confirm({
      title: t('editions.deleteAllConfirm'),
      description: t('editions.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t('editions.deleteErrorMessage'), {
        variant: 'error',
      });
      return;
    }
    await doDeleteAllEditions()
      .then(() => {
        enqueueSnackbar(t('editions.deleteSuccessfulMessage'), {
          variant: 'success',
        });
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`editions.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleToggleActiveEdition = async (id: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const isActive = id === activeEditionId;
    const { confirmed } = await confirm({
      title: isActive
        ? t('editions.deactivateEditionConfirm')
        : t('editions.activateEditionConfirm'),
      description: t('editions.toggleEditionDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(
        isActive
          ? t('editions.deactivateEditionErrorMessage')
          : t('editions.activateEditionErrorMessage'),
        {
          variant: 'error',
        },
      );
      return;
    }
    await doToggleActiveEdition(id);

    enqueueSnackbar(
      isActive
        ? t('editions.deactivateEditionSuccessfulMessage')
        : t('editions.activateEditionSuccessfulMessage'),
      {
        variant: 'success',
      },
    );
  };

  const handleSaveStatisticsForEdition = async (id: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    setIsLoadingEdition({ [id]: true });
    await doSaveStatisticsForEdition(id);
    setIsLoadingEdition({ [id]: false });
  };

  return (
    <>
      <DashboardListPageLayout
        title={t('editions.pageTitle')}
        isLoading={isLoadingSystemOptions}
        scrollable
        pageBoxSx={ticketCardGridContainerPaddingSx}
        toolbar={<ViewSwitch view={view} onSetView={setView} />}
        toolbarAsideVariant="end"
        headerActions={
          <>
            {isSuperAdmin && (
              <Tooltip title={t('editions.deleteAllEditions')}>
                <IconButton onClick={handleDeleteAllEditions} color="error">
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            )}
            <Button
              {...dashboardPrimaryActionButtonProps()}
              onClick={() => handleOpenEditionEditorModal()}
              startIcon={<AddIcon />}
            >
              {t('editions.new')}
            </Button>
          </>
        }
      >
        {!isLoadingSystemOptions && editions.length === 0 && (
          <Empty
            icon={<EventOutlinedIcon />}
            title={t('empty.editions.title')}
            description={t('empty.editions.description')}
            primaryAction={{
              label: t('empty.addNew'),
              onClick: () => handleOpenEditionEditorModal(),
            }}
          />
        )}
        {!!editions.length && (
          <>
            {view === EView.grid && (
              <Grid container spacing={2} sx={{ overflow: 'visible' }}>
                {map(editions, edition => (
                  <Grid size={{ xs: 12, md: 6 }} key={edition.id} sx={ticketCardGridItemSx}>
                    <EditionCard
                      isLoading={isLoadingEdition ? isLoadingEdition[edition.id] : false}
                      activeEditionId={activeEditionId}
                      edition={edition}
                      onDeleteEdition={handleDeleteEdition}
                      onEditEdition={handleOpenEditionEditorModal}
                      onSaveEdition={saveEdition}
                      onToggleActiveEdition={handleToggleActiveEdition}
                      onSaveStatistics={handleSaveStatisticsForEdition}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            {view === EView.table && (
              <EditionsTable
                activeEditionId={activeEditionId}
                editions={editions}
                isLoading={isLoadingEdition}
                onDeleteEdition={handleDeleteEdition}
                onEditEdition={handleOpenEditionEditorModal}
                onSaveEdition={saveEdition}
                onSaveStatistics={handleSaveStatisticsForEdition}
                onToggleActiveEdition={handleToggleActiveEdition}
              />
            )}
            <EditionAccountingSection
              editions={editions}
              selectedEdition={accountingEdition}
              toolbar={
                <EditionYearFilter
                  selectedYear={accountingYear}
                  onYearChange={setAccountingYear}
                  id="accounting-edition-year"
                />
              }
            />
          </>
        )}
      </DashboardListPageLayout>
      {editionEditorModalOpen && (
        <EditionEditorModal
          id="edition-editor"
          open={editionEditorModalOpen}
          handleClose={handleCloseEditionEditorModal}
          handleSave={saveEdition}
          editionToEdit={editionToEdit}
        />
      )}
    </>
  );
};

export default Editions;
